import { supabase } from '../supabaseClient';

// Curated, deliberately conservative mapping from resource_directory.category values to
// the closest trade_career_data.trade name. Several common categories (Plumbing, Carpentry)
// have no genuinely equivalent entry in the current trade dataset and are intentionally left
// unmapped here rather than guessing at a stand-in -- showing no snippet is better than
// showing a misleading one. Revisit this list as trade_career_data grows.
// Note: 'EV/Hybrid' (general automotive EV work) is intentionally NOT mapped -- distinct from
// 'Industrial/EV/Hybrid' below (Hybrid Diesel Equipment Technology, a diesel specialization).
export const CATEGORY_TO_TRADE = {
  'Agriculture':            'Agricultural Equipment Technician',
  'Automotive':             'Automotive Technician',
  'Aviation Mechanic':      'Aircraft Mechanic (A&P)',
  'CDL':                    'CDL/Truck Driver',
  'CNC':                    'Machinist/CNC Machinist',
  'CNC Machining':          'Machinist/CNC Machinist',
  'Collision Repair':       'Automotive Body Repairer',
  'Diesel Technician':      'Diesel Mechanic',
  'Fire Protection':        'Pipefitter/Steamfitter',
  'Heavy Equipment':        'Heavy Equipment Operator',
  'HVAC':                   'HVAC Technician',
  'Industrial Maintenance': 'Industrial Maintenance Mechanic',
  'Industrial/EV/Hybrid':   'Diesel Mechanic',
  'Manufacturing':          'Machine Operator',
  'Masonry':                'Mason/Bricklayer',
  'Sheet Metal':            'Sheet Metal Worker',
  'Ironwork':                'Ironworker',
  'Pipefitting':            'Pipefitter/Steamfitter',
  'Welding':                'Welder and Fabricator',
  'Electrical':             'Industrial Electrician',
  'Mechatronics':           'Automation & Robotics Technician',
  'Operating Engineers':    'Heavy Equipment Operator',
};

// Canonical alignment between the three trade-shaped tables (trade_resources, resource_directory,
// trade_career_data) used to build the "Trade Hub" consolidated view. Verified directly against
// the database -- do not re-derive or guess at these. Deliberately scoped to just the 9 trades
// with real trade_resources content, since those are the only ones with the narrative (career
// path, certs, apprenticeship length) that makes a hub meaningful. Note resource_directory's
// category values here sometimes differ from trade_resources.category (e.g. CDL / Truck Driver's
// trade_resources.category is "Transportation", but resource_directory uses "CDL") -- always use
// this table's `category`, never trade_resources.category, when matching schools/unions.
export const TRADE_HUB_TRADES = [
  { hubTrade: 'Carpenter',            category: 'Carpentry',       payTrade: 'Carpenter' },
  { hubTrade: 'CDL / Truck Driver',   category: 'CDL',              payTrade: 'CDL/Truck Driver' },
  { hubTrade: 'Electrician',          category: 'Electrical',       payTrade: 'Industrial Electrician' },
  { hubTrade: 'HVAC Technician',      category: 'HVAC',             payTrade: 'HVAC Technician' },
  { hubTrade: 'Ironworker',           category: 'Ironwork',         payTrade: 'Ironworker' },
  { hubTrade: 'Operating Engineer',   category: 'Heavy Equipment',  payTrade: 'Heavy Equipment Operator' },
  { hubTrade: 'Plumber / Pipefitter', category: 'Plumbing',         payTrade: 'Pipefitter/Steamfitter' },
  { hubTrade: 'Sheet Metal Worker',   category: 'Sheet Metal',      payTrade: 'Sheet Metal Worker' },
  { hubTrade: 'Welder',               category: 'Welding',          payTrade: 'Welder and Fabricator' },
];

export function hubTradeForPayTrade(payTrade) {
  return TRADE_HUB_TRADES.find(e => e.payTrade === payTrade) || null;
}


// The two trade_career_data measurement types are genuinely different statistics and must
// never share a label, color, or heading -- that's the whole point of surfacing this
// distinction instead of just printing three numbers. Shared by CareerPayGuide's detail panel
// and the Trade Hub's pay section (both render via TradePayDetailCard).
// Display text lives in i18n (src/i18n/en.json / es.json under "resources.measurement") --
// this plain module-level constant can't call useTranslation() itself, so it stores key
// references, and TradePayDetailCard (a real component) resolves them via t() at render time.
export const MEASUREMENT_INFO = {
  percentile_10_50_90: {
    headingKey: 'resources.measurement.percentileHeading',
    chipLabelKey: 'resources.measurement.percentileChip',
    chipColor: '#4A9EFF',
    columns: [
      { key: 'entry_pay',  labelKey: 'resources.measurement.percentile10' },
      { key: 'median_pay', labelKey: 'resources.measurement.median50' },
      { key: 'top_pay',    labelKey: 'resources.measurement.percentile90' },
    ],
    noteKey: null,
  },
  entry_median_experienced: {
    headingKey: 'resources.measurement.progressionHeading',
    chipLabelKey: 'resources.measurement.progressionChip',
    chipColor: '#B794F6',
    columns: [
      { key: 'entry_pay',  labelKey: 'resources.measurement.entryLevel' },
      { key: 'median_pay', labelKey: 'resources.measurement.median' },
      { key: 'top_pay',    labelKey: 'resources.measurement.experienced' },
    ],
    noteKey: 'resources.measurement.progressionNote',
  },
};

// "Popular trades" tiles at the top of the Career Pay Guide, in display order. Exact
// trade_career_data.trade names -- a name with no row for the selected state is simply skipped.
export const POPULAR_PAY_TRADES = [
  'Electrician', 'Plumber', 'Carpenter', 'HVAC Technician',
  'Welder and Fabricator', 'Cement Mason', 'Construction Laborer', 'Heavy Equipment Operator',
];

// Trade the Career Pay Guide opens on: Electrician, else the first popular trade that has a
// row, else the first row in `rows` (already filtered to one state and sorted for display).
export function defaultPayTrade(rows) {
  if (!rows.length) return '';
  const names = new Set(rows.map(r => r.trade));
  return POPULAR_PAY_TRADES.find(name => names.has(name)) || rows[0].trade;
}

const STATE_NAMES = { IL: 'Illinois' };

export function stateName(code) {
  return STATE_NAMES[code] || code;
}

// "Chicago-Naperville-Elgin" -> "Chicago", "Cook County (Chicago)" -> "Cook County".
// Falls back to the state name when no metro_area is set (a statewide figure).
export function shortLocation(row) {
  if (row.metro_area) return row.metro_area.split(/[-(]/)[0].trim();
  return stateName(row.state);
}

export function fmtCurrency(n) {
  if (n == null) return null;
  return Math.round(n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

export async function fetchTradeCareerData() {
  const { data, error } = await supabase
    .from('trade_career_data')
    .select('*')
    .order('trade', { ascending: true });
  return error ? [] : (data || []);
}

// Builds a trade-name -> row lookup, preferring state='IL' when a trade somehow has more
// than one row (none do today, but this keeps the lookup well-defined as more states land).
export function buildTradeLookup(rows) {
  const map = new Map();
  for (const row of rows) {
    const existing = map.get(row.trade);
    if (!existing || (row.state === 'IL' && existing.state !== 'IL')) map.set(row.trade, row);
  }
  return map;
}
